/**
 * Single source of truth for every PostHog event name and its payload shape.
 * Add new events here first — `track()` in analytics.ts only accepts names
 * declared in this map, so event names can't drift or typo across call sites.
 */

export const ANALYTICS_EVENTS = {
  EDUCATIONAL_RENDER_FAILURE: 'educational_render_failure',
  // Landing page
  // "Page viewed" is PostHog's built-in $pageview (fired app-wide by
  // PostHogPageviewTracker in App.tsx) — no separate custom event needed.
  SCROLL_DEPTH: "scroll_depth",
  TIME_ON_PAGE: "time_on_page",
  HERO_VIEWED: "hero_viewed",
  HERO_CTA_VIEWED: "hero_cta_viewed",
  HERO_CTA_CLICKED: "hero_cta_clicked",
  SECONDARY_CTA_CLICKED: "secondary_cta_clicked",
  NAV_LINK_CLICKED: "nav_link_clicked",
  LOGO_CLICKED: "logo_clicked",
  FOOTER_LINK_CLICKED: "footer_link_clicked",
  LANDING_VARIANT_ASSIGNED: "landing_variant_assigned",
  LANDING_DEMO_ANSWERED: "landing_demo_answered",

  // Engagement
  SECTION_VIEWED: "section_viewed",
  FEATURE_CARD_VIEWED: "feature_card_viewed",
  FEATURE_CARD_CLICKED: "feature_card_clicked",
  STUDY_DAY_OPENED: "study_day_opened",
  STUDY_QUIZ_COMPLETED: "study_quiz_completed",
  PRICING_VIEWED: "pricing_viewed",
  PRICING_CTA_CLICKED: "pricing_cta_clicked",
  KIDS_MODE_CTA_CLICKED: "kids_mode_cta_clicked",
  WAITLIST_JOINED: "waitlist_joined",
  WAITLIST_FAILED: "waitlist_failed",

  // Forms (generic, reusable across any form via useFormAnalytics)
  FORM_STARTED: "form_started",
  FORM_FIELD_FOCUSED: "form_field_focused",
  FORM_FIELD_COMPLETED: "form_field_completed",
  FORM_VALIDATION_ERROR: "form_validation_error",
  FORM_ABANDONED: "form_abandoned",
  FORM_SUBMITTED: "form_submitted",
  FORM_SUCCESS: "form_success",
  FORM_FAILED: "form_failed",

  // Authentication
  SIGNUP_STARTED: "signup_started",
  SIGNUP_COMPLETED: "signup_completed",
  SIGNUP_FAILED: "signup_failed",
  LOGIN_STARTED: "login_started",
  LOGIN_COMPLETED: "login_completed",
  LOGIN_FAILED: "login_failed",
  SOCIAL_AUTH_CLICKED: "social_auth_clicked",
  PASSWORD_RESET_REQUESTED: "password_reset_requested",
  PASSWORD_RESET_COMPLETED: "password_reset_completed",
  SIGNUP_CONFIRMATION_RESENT: "signup_confirmation_resent",

  // Errors
  API_ERROR: "api_error",
  AUTH_ERROR: "auth_error",
  JS_EXCEPTION: "js_exception",
  ROUTE_ERROR: "route_error",

  // Performance
  PUBLIC_STUDY_PAGE_VIEWED: "public_study_page_viewed",
  PUBLIC_STUDY_CTA_CLICKED: "public_study_cta_clicked",
  WEB_VITAL: "web_vital",
  SLOW_API_REQUEST: "slow_api_request",

  // Loading experience (NovariMarkLoader rollout — see useLoadingScreenAnalytics)
  LOADING_SCREEN_SHOWN: "loading_screen_shown",
  LOADING_SCREEN_RESOLVED: "loading_screen_resolved",
  LOADING_SCREEN_TIMEOUT: "loading_screen_timeout",

  // Appearance design test (see lib/appearanceTracking.ts)
  APPEARANCE_CHOICE_MADE: "appearance_choice_made",
  APPEARANCE_PICKER_SHOWN: "appearance_picker_shown",
  APPEARANCE_PICKER_CLOSED: "appearance_picker_closed",

  // Study From Questions (server records extraction/creation events; these
  // are the choices only the client sees). Never include question text.
  QUESTION_SOURCE_CHOSEN: "question_source_chosen",
  QUESTION_ORIGINAL_VIEWED: "question_original_viewed",
  QUESTION_OUTPUT_CHOSEN: "question_output_chosen",
  // Take this paper (counts and choices only — never question or answer text).
  PAPER_DETECTED: "paper_detected",
  PAPER_TAKE_STARTED: "paper_take_started",
  PAPER_ATTEMPT_STARTED: "paper_attempt_started",
  PAPER_ATTEMPT_RESUMED: "paper_attempt_resumed",
  PAPER_ATTEMPT_SUBMITTED: "paper_attempt_submitted",
  PAPER_ATTEMPT_RETRIED: "paper_attempt_retried",
  PAPER_MARK_SCHEME_ADDED: "paper_mark_scheme_added",
  PAPER_SOURCE_VIEWED: "paper_source_viewed",
} as const;

export type AnalyticsEventName =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export type AnalyticsProperties = Record<string, string | number | boolean | null | undefined>;
